/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import mbpLogger from '@module-logger/mbp';
import { useQuery } from '@apollo/client';
import { object, string } from 'prop-types';
import Link from '@material-ui/core/Link';
import ReactMarkdown from 'react-markdown';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useUIDSeed } from 'react-uid';

import findContentByUID from '../../../gql/queries/findContentByUID';
import { getBrandName } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import parseFontFamilyExtension from '../../../helpers/contentstack/parseFontFamilyExtension';
import { emitCustomTrackingEvent } from '../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { setReminder } from '../../../../state/ducks/App/App-Actions';
import { getPersonalizedReminder, getSSRDeviceType } from '../../../../state/ducks/App/App-Selectors';
import useGetDerivedReminders from '../../../helpers/Personalization/useGetDerivedReminders';

const useStyles = makeStyles((theme) => ({
    section: (styles) => ({
        padding: styles?.padding || '0',
    }),
    container: {
        margin: '24px 0px 0px',
        '& $chip:not(:last-of-type)': {
            borderRight: '1px solid #D2CDE4',
        },
    },
    scroller: {
        display: 'flex',
        gap: '10px',
        [theme.breakpoints.down(900)]: {
            overflowX: 'auto',
            paddingBottom: '4px',
        },
        [theme.breakpoints.up(900)]: {
            justifyContent: 'center',
        },
    },
    centered: {
        justifyContent: 'center',
    },
    heading: (styles) => ({
        '& p, h2, h3,h4': {
            fontSize: styles.heading?.font_size || '24px',
            color: styles.heading?.color?.color || '',
            fontFamily: parseFontFamilyExtension(styles.heading?.font_family) || 'Lato,sans-serif',
            textAlign: 'center',
        },
    }),
    roundedBox: (styles) => ({
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        background: styles?.backgroundColor || '#F5F5F5',
        borderRadius: '32px',
    }),
    chip: (styles) => ({
        display: 'flex',
        alignItems: 'center',
        padding: '8px 16px',
        cursor: 'pointer',
        overflow: 'hidden',
        fontSize: styles.pills?.font_size || '16px',
        color: styles.pills?.color?.color || '',
        fontFamily: parseFontFamilyExtension(styles.pills?.font_family) || 'Lato,sans-serif',
        [theme.breakpoints.down(900)]: {
            textWrap: 'nowrap',
        },
    }),
    eventName: {
        textTransform: 'capitalize',
    },
    icon: (styles) => ({
        display: 'flex',
        justifyContent: 'center',
        '& img': {
            width: styles.addIconStyles?.width || '32px',
        },
    }),
    date: {
        marginLeft: '8px',
        fontSize: '12px',
    },
    peopleIcon: (styles) => ({
        marginRight: '12px',
        background: styles?.backgroundColor || '#F5F5F5',
        borderRadius: '50%',
        '& img': {
            width: '24px',
        },
    }),
    hoverdChip: (styles) => ({
        '&:hover': {
            backgroundColor: styles.pills?.color?.color || '#201648',
            color: '#fff !important',
            border: 0,
            borderRadius: '39px',
            '& $peopleIcon img': {
                objectFit: 'none',
            },
            '& $peopleIcon': {
                width: '23px',
                height: '23px',
            },
        },
    }),
    selected: (styles) => ({
        backgroundColor: styles.pills?.color?.color || '#201648',
        color: '#fff !important',
        border: 0,
        borderRadius: '39px',
        '& $peopleIcon': {
            width: '23px',
            height: '23px',
        },
        '& $peopleIcon img': {
            objectFit: 'none',
        },
    }),
    rounded: (styles) => ({
        background: styles?.backgroundColor || '#F5F5F5',
        padding: styles.addIconStyles?.padding || '12px',
        borderRadius: '39px',
        [theme.breakpoints.down(600)]: {
            padding: styles.addIconStyles?.padding || '8px',
        },
    }),
    create: {
        display: 'flex',
        alignItems: 'center',
        '& span': {
            marginRight: '16px',
        },
        '&:hover': {
            textDecoration: 'none',
        },
    },
}));

const formatDate = (dateString) => {
    const date = new Date(`${dateString}T00:00` || '');
    return format(date, 'MMM dd').toUpperCase(); // Converts to "APR 03"
};

const Icon = ({ className, url, alt }) => (
    <div className={className}>
        <img src={url} alt={alt} />
    </div>
);

Icon.propTypes = {
    className: string.isRequired,
    url: string.isRequired,
    alt: string.isRequired,
};

const EventChips = ({
    reminderData, cmsData,
}) => {
    const styles = {
        padding: cmsData?.padding,
        backgroundColor: cmsData?.background_color?.color,
        heading: cmsData?.heading,
        pills: cmsData?.pills,
        addIconStyles: cmsData?.add_icon_style,
    };

    const dispatch = useDispatch();
    const personalizedReminder = useSelector(getPersonalizedReminder);
    const classes = useStyles(styles);
    const isMobile = useMediaQuery('(max-width: 600px)');
    const seed = useUIDSeed();
    const ssrDevice = useSelector(getSSRDeviceType);

    const recipients = reminderData?.reminderRecs?.recipients || [];
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const headingCopy = cmsData?.heading?.copy;

    useEffect(() => {
        dispatch(emitCustomTrackingEvent({
            action: 'Impression',
            eventSubType: 'feature-tracking',
            category: 'Reminders',
            page: { type: 'home' },
        }));

        if (personalizedReminder?.active) {
            setSelectedIndex(personalizedReminder?.selectedIndex);
        }
    }, []);

    const handleClick = (index) => {
        if (index === selectedIndex) {
            setSelectedIndex(-1);
            dispatch(
                setReminder({ active: null, selectedIndex: 0 }),
            );
            return;
        }

        setSelectedIndex(index);
        const reminder = recipients[index];

        dispatch(emitCustomTrackingEvent({
            action: 'Click',
            eventSubType: 'feature-tracking',
            category: 'Reminders',
            firstName: reminder?.firstName,
            lastName: reminder?.lastName,
            occasionCode: reminder?.occasionCode,
            page: { type: 'home' },
            partNumber: reminder?.product?.partNumber,
        }));

        dispatch(
            setReminder({ active: reminder, selectedIndex: index }),
        );
    };

    const renderAddIcon = () => (
        <Link href={cmsData?.create_reminder?.href} className={classes.create}>
            <Icon url={cmsData?.add_icon?.url} className={`${classes.icon} ${classes.rounded}`} alt="add-icon" />
        </Link>
    );

    const slicedRecipients = recipients?.slice(0, cmsData?.pills_to_show || 4) || [];
    const isRecipientsAvailable = slicedRecipients?.length > 0;

    return (
        <section className={classes.section}>
            {headingCopy && <ReactMarkdown source={headingCopy} escapeHtml={false} className={classes.heading} />}

            <div className={classes.container}>
                <div className={`${classes.scroller} ${isRecipientsAvailable ? '' : classes.centered}`}>
                    {(isRecipientsAvailable && isMobile) && renderAddIcon()}

                    <div className={classes.roundedBox}>
                        {
                            isRecipientsAvailable
                                ? slicedRecipients.map((recipient, index) => {
                                    const eventName = recipient.firstName
                                        ? `${recipient.firstName}'s ${recipient.occasion}`
                                        : recipient.occasion;
                                    const eventDate = formatDate(recipient.occasionDate);

                                    return (
                                        <div
                                            key={seed(recipient)}
                                            role="button"
                                            onKeyDown={() => { }}
                                            tabIndex={index}
                                            onClick={() => handleClick(index)}
                                            className={`${classes.chip} ${ssrDevice === 'desktop' ? classes.hoverdChip : ''} ${selectedIndex === index ? classes.selected : ''}`}
                                        >
                                            <Icon url={cmsData?.people_icon?.url} className={`${classes.icon} ${classes.peopleIcon}`} alt="people" />
                                            <span className={classes.eventName}>{eventName}</span>
                                            <span className={classes.date}>{eventDate}</span>
                                        </div>
                                    );
                                })
                                : (
                                    <Link href={cmsData?.create_reminder?.href} className={`${classes.chip} ${classes.create}`}>
                                        <Icon url={cmsData?.people_icon?.url} className={`${classes.icon} ${classes.peopleIcon}`} alt="people" />
                                        <span>{cmsData?.create_reminder?.title}</span>
                                        <Icon url={cmsData?.add_icon?.url} className={classes.icon} alt="add-icon" />
                                    </Link>
                                )
                        }

                    </div>
                    {(isRecipientsAvailable && !isMobile) && renderAddIcon()}
                </div>
            </div>
        </section>
    );
};

EventChips.propTypes = {
    reminderData: object.isRequired,
    cmsData: object.isRequired,
};

function PersonalizedReminders({
    blockData,
}) {
    const brand = useSelector(getBrandName) || '1800flowers';

    const { _content_type_uid, uid } = blockData?.reference?.[0] || {};
    const CONTENT_QUERY = findContentByUID({
        brand,
        uid,
        contentType: _content_type_uid,
    });

    const {
        data, error,
    } = useQuery(CONTENT_QUERY);

    const { data: dats, error: reminderError } = useGetDerivedReminders({
        queryName: 'RemindersQuery',
        optInHoldoutParticipation: true,
        resolveIdentity: true,
    });

    if (error || reminderError) {
        mbpLogger.logError({
            function: 'PersonalizedReminders',
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'PersonalizedReminders',
            jsError: error,
        });
        return null;
    }

    if (data?.findContentByUID?.content) {
        const cmsData = data?.findContentByUID?.content?.entry || {};

        return <EventChips reminderData={dats} cmsData={cmsData} />;
    }

    return null;
}

PersonalizedReminders.propTypes = {
    blockData: object.isRequired,
};

export default PersonalizedReminders;
